/**
 * TurnoChangeSection - Cambio de turno (Empresa + Compañeros CT/CF)
 * Sub-componente presentacional extraído de DayEditModal
 */
import { Check, Loader2, Sun, Moon, Clock, Zap, Trash2, ChevronDown, ChevronUp, Briefcase, Search, UserPlus } from 'lucide-react';

export default function TurnoChangeSection({
    showTurnoSection,
    setShowTurnoSection,
    blockTurnoChange,
    hasActiveTurnoChange,
    filteredTurnos,
    turnos,
    selectedTurno,
    setSelectedTurno,
    existingMarca,
    exchangeInfo,
    shiftOverride,
    // CT autocomplete
    conductoresList,
    ctPeerInput,
    setCtPeerInput,
    ctPeerId,
    setCtPeerId,
    ctSearchResults,
    setCtSearchResults,
    showCtAutocomplete,
    setShowCtAutocomplete,
    // CF
    cfMode,
    setCfMode,
    deudores,
    loadingDeudores,
    cfCompaneroId,
    setCfCompaneroId,
    cfCompaneroNombre,
    setCfCompaneroNombre,
    // Handlers
    handleDeleteTurnoOnly,
    deleting
}) {
    const handleConductorSearch = (val) => {
        setCtPeerInput(val);
        setCtPeerId(null);
        if (val.trim().length > 0) {
            const searchStr = val.toLowerCase();
            const filtered = conductoresList.filter(c =>
                c.nombre?.toLowerCase().includes(searchStr) ||
                c.nick?.toLowerCase().includes(searchStr)
            ).slice(0, 5);
            setCtSearchResults(filtered);
            setShowCtAutocomplete(true);
        } else {
            setCtSearchResults([]);
            setShowCtAutocomplete(false);
        }
    };

    const handleSelectConductor = (comp) => {
        setCtPeerInput(comp.nick || comp.nombre);
        setCtPeerId(comp.id);
        setShowCtAutocomplete(false);
    };

    const renderAutocompleteDropdown = () => (
        showCtAutocomplete && (
            <div className="absolute top-full left-0 right-0 mt-1 border border-cyan-500/30 rounded-lg shadow-xl overflow-hidden z-20" style={{ background: 'var(--bg-secondary)' }}>
                {ctSearchResults.length > 0 ? (
                    ctSearchResults.map(comp => (
                        <button
                            key={comp.id}
                            className="w-full text-left px-4 py-3 text-sm border-b transition-all border-white/5 last:border-0 hover:bg-cyan-500/10"
                            style={{ color: 'var(--text-primary)' }}
                            onClick={() => handleSelectConductor(comp)}
                        >
                            <span className="font-black">{comp.nick || comp.nombre}</span>
                            {comp.nick && comp.nombre && <span className="text-[var(--text-secondary)] text-xs ml-2">({comp.nombre})</span>}
                        </button>
                    ))
                ) : (
                    <div className="px-4 py-3 text-sm flex items-start gap-3 bg-white/5">
                        <UserPlus className="w-5 h-5 text-[var(--text-muted)] shrink-0 mt-0.5" />
                        <div>
                            <p className="text-white font-medium">Añadir a "{ctPeerInput}" manualmente</p>
                            <p className="text-xs text-[var(--text-muted)] mt-1">No está en el sistema. Se guardará con este nombre.</p>
                        </div>
                    </div>
                )}
            </div>
        )
    );

    const renderConductorSearchInput = (placeholder = "Escribe su nombre, nick o alias...") => (
        <div className="relative">
            <div className="flex items-center border rounded-xl focus-within:border-cyan-500 transition-colors" style={{ background: 'var(--bg-input)', borderColor: 'var(--border-color)' }}>
                <Search className="w-4 h-4 text-[var(--text-muted)] ml-3" />
                <input
                    type="text"
                    placeholder={placeholder}
                    value={ctPeerInput}
                    onChange={(e) => handleConductorSearch(e.target.value)}
                    onFocus={() => { if (ctPeerInput.trim().length > 0) setShowCtAutocomplete(true); }}
                    onBlur={() => setTimeout(() => setShowCtAutocomplete(false), 200)}
                    className="w-full bg-transparent px-3 py-3 text-sm focus:outline-none"
                    style={{ color: 'var(--text-primary)' }}
                />
            </div>
            {renderAutocompleteDropdown()}
            {ctPeerId && (
                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                    <Check className="w-3 h-3" /> Compañero vinculado correctamente
                </p>
            )}
        </div>
    );

    return (
        <div className={`border rounded-xl overflow-hidden ${blockTurnoChange ? 'border-gray-600/20 opacity-60' : 'border-purple-500/20'}`}>
            <button
                id="tour-edit-turno"
                onClick={() => !blockTurnoChange && setShowTurnoSection(!showTurnoSection)}
                disabled={blockTurnoChange}
                className={`w-full flex items-center justify-between p-3 transition-colors border-b ${blockTurnoChange
                    ? 'bg-gray-500/10 cursor-not-allowed opacity-50'
                    : 'bg-purple-500/10 hover:bg-purple-500/20'}`}
                style={{ borderColor: 'rgba(168, 85, 247, 0.1)' }}
            >
                <span className={`flex items-center gap-2 font-bold ${blockTurnoChange ? 'text-[var(--text-muted)]' : 'text-purple-600 dark:text-purple-400'}`}>
                    <Clock className="w-4 h-4" />
                    Cambiar turno
                    {blockTurnoChange && <span className="text-[10px] bg-gray-500/20 px-2 py-0.5 rounded-full uppercase">Bloqueado</span>}
                    {!blockTurnoChange && selectedTurno && <span className="text-[10px] bg-purple-500/20 px-2 py-0.5 rounded-full uppercase">Modificado</span>}
                </span>
                {!blockTurnoChange && (showTurnoSection ? <ChevronUp className="w-4 h-4 text-purple-500" /> : <ChevronDown className="w-4 h-4 text-purple-500" />)}
            </button>

            {blockTurnoChange && (
                <div className="p-3 bg-gray-500/5">
                    <p className="text-xs text-[var(--text-secondary)]">Este día ya tiene un cambio activo. No se puede modificar el turno.</p>
                </div>
            )}

            {!blockTurnoChange && showTurnoSection && (
                <div className="p-3 space-y-3">
                    {hasActiveTurnoChange ? (
                        <div className="flex flex-col items-center justify-center p-4 bg-white/5 border border-white/10 rounded-xl">
                            <div className="w-12 h-12 rounded-full bg-orange-500/20 text-orange-500 flex items-center justify-center mb-3 shadow-[0_0_15px_rgba(249,115,22,0.2)]">
                                <Clock className="w-6 h-6" />
                            </div>
                            <p className="text-xs text-[var(--text-muted)] font-bold uppercase tracking-widest mb-1">Cambio de Turno Activo</p>
                            <p className="text-xl font-black text-orange-500 mb-3">
                                {turnos.find(t => t.id === existingMarca.turno_id)?.nombre || "Modificado"}
                            </p>

                            {(exchangeInfo || ['CT', 'CF'].includes(shiftOverride)) && (
                                <div className="w-full flex items-center justify-center mb-4">
                                    <p className="text-xs bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-3 py-2 rounded-full font-bold uppercase tracking-widest flex items-center gap-2">
                                        🔄 {exchangeInfo ? (exchangeInfo.tipo === 'CT' ? 'Trabajas para' : 'Libra por') : (shiftOverride === 'CT' ? 'Trabajas para' : 'Libra por')} {exchangeInfo ? exchangeInfo.partner : '(Sin especificar)'}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={(e) => { e.preventDefault(); handleDeleteTurnoOnly(); }}
                                disabled={deleting}
                                className="w-full max-w-[200px] py-2.5 rounded-xl bg-red-500/10 text-red-500 hover:bg-red-500/20 hover:text-red-400 border border-red-500/20 transition-colors flex items-center justify-center gap-2 font-bold text-sm shadow-sm"
                            >
                                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                Eliminar Cambio
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-5 mt-2">

                            {/* Grupo 1: Empresa */}
                            {filteredTurnos.some(t => !['CT', 'CF'].includes(t.nombre)) && (
                                <div>
                                    <p className="text-[10px] text-indigo-600 dark:text-indigo-400 font-black uppercase tracking-widest flex items-center gap-1.5 mb-3 ml-1">
                                        <Briefcase className="w-3.5 h-3.5" /> A petición de empresa
                                    </p>
                                    <div className="grid grid-cols-2 gap-2">
                                        {filteredTurnos.filter(t => !['CT', 'CF'].includes(t.nombre)).map((turno) => {
                                            const Icon = turno.nombre.includes('Mañana') ? Sun :
                                                turno.nombre.includes('Tarde') ? Moon :
                                                    turno.nombre.includes('Noche') ? Moon : Zap;
                                            const isSelected = selectedTurno === turno.id;

                                            return (
                                                <button
                                                    key={turno.id}
                                                    title="Trabajas para la empresa. Genera 1 día de saldo a tu favor."
                                                    onClick={() => setSelectedTurno(isSelected ? null : turno.id)}
                                                    className={`flex items-center gap-2.5 p-4 rounded-2xl transition-all text-left border ${isSelected
                                                        ? 'bg-indigo-500/15 border-indigo-500/50 text-indigo-700 dark:text-indigo-300 shadow-lg shadow-indigo-500/10 ring-1 ring-indigo-500/20'
                                                        : 'border-transparent hover:bg-white/10'
                                                        }`}
                                                    style={{ background: isSelected ? 'rgba(99, 102, 241, 0.15)' : 'var(--bg-input)' }}
                                                >
                                                    <Icon className={`w-4 h-4 ${isSelected ? 'text-indigo-600 dark:text-indigo-400' : 'text-[var(--text-muted)]'}`} />
                                                    <span className="text-sm font-black uppercase tracking-tighter" style={{ color: isSelected ? 'inherit' : 'var(--text-primary)' }}>{turno.nombre}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Grupo 2: Compañeros */}
                            {filteredTurnos.some(t => ['CT', 'CF'].includes(t.nombre)) && (
                                <div>
                                    <p className="text-[10px] text-teal-600 dark:text-teal-400 font-black uppercase tracking-widest flex items-center gap-1.5 mb-3 ml-1">
                                        <UserPlus className="w-3.5 h-3.5" /> Cambios con compañeros
                                    </p>
                                    <div className="grid grid-cols-1 gap-2">
                                        {filteredTurnos.filter(t => ['CT', 'CF'].includes(t.nombre)).map((turno) => {
                                            const isSelected = selectedTurno === turno.id;

                                            return (
                                                <button
                                                    key={turno.id}
                                                    title={turno.nombre === 'CT' ? 'Trabajas tú para un compañero. Ganas 1 día.' : 'El compañero trabaja por ti. Consumes 1 día.'}
                                                    onClick={() => setSelectedTurno(isSelected ? null : turno.id)}
                                                    className={`flex items-center justify-between p-4 rounded-2xl transition-all text-left border ${isSelected
                                                        ? 'bg-teal-500/15 border-teal-500/50 text-teal-700 dark:text-teal-300 shadow-lg shadow-teal-500/10 ring-1 ring-teal-500/20'
                                                        : 'border-transparent hover:bg-white/10'
                                                        }`}
                                                    style={{ background: isSelected ? 'rgba(20, 184, 166, 0.15)' : 'var(--bg-input)' }}
                                                >
                                                    <div className="flex items-center gap-2.5">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-teal-500/20' : 'bg-white/10'}`}>
                                                            <Zap className={`w-4 h-4 ${isSelected ? 'text-teal-600 dark:text-teal-400' : 'text-[var(--text-muted)]'}`} />
                                                        </div>
                                                        <span className="text-sm font-black uppercase tracking-widest" style={{ color: isSelected ? 'inherit' : 'var(--text-primary)' }}>{turno.nombre}</span>
                                                    </div>
                                                    <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full border shadow-sm ${isSelected ? 'bg-teal-500/10 border-teal-500/40 text-teal-700 dark:text-teal-300' : 'bg-black/5 dark:bg-black/20 border-white/10 text-gray-600 dark:text-[var(--text-muted)]'}`}>
                                                        {turno.nombre === 'CT' ? '+1 saldo' : '-1 saldo'}
                                                    </span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Extra form for CT selection */}
                    {selectedTurno && turnos.find(t => t.id === selectedTurno)?.nombre === 'CT' && (
                        <div className="mt-4 p-4 rounded-2xl border border-cyan-500/40 bg-cyan-500/5 shadow-inner">
                            <label className="block text-[10px] font-black text-cyan-700 dark:text-cyan-400 uppercase tracking-widest mb-3">Compañero del cambio:</label>
                            {renderConductorSearchInput("Escribe su nombre, nick o alias...")}
                        </div>
                    )}

                    {/* Extra form for CF selection */}
                    {selectedTurno && turnos.find(t => t.id === selectedTurno)?.nombre === 'CF' && (
                        <div className="mt-4 p-4 rounded-2xl border border-green-500/40 bg-green-500/5 shadow-inner">
                            <label className="block text-[10px] font-black text-green-700 dark:text-green-400 uppercase tracking-widest mb-4">¿Quién te cubre?</label>

                            {/* Mode selector */}
                            <div className="grid grid-cols-2 gap-2 mb-3">
                                <button
                                    onClick={() => { setCfMode('cobrar'); setCtPeerInput(''); setCtPeerId(null); }}
                                    className={`p-3 rounded-xl text-xs font-black uppercase tracking-tighter border transition-all flex flex-col items-center gap-1 ${cfMode === 'cobrar'
                                        ? 'bg-amber-500/20 border-amber-500/60 text-amber-800 dark:text-amber-400 shadow-md shadow-amber-500/20'
                                        : 'border-transparent text-amber-700 dark:text-amber-500'}`}
                                    style={{ background: cfMode === 'cobrar' ? 'rgba(245, 158, 11, 0.2)' : 'var(--bg-secondary)' }}
                                >
                                    <div className="flex items-center gap-2">
                                        <Clock className="w-3 h-3" /> Cobrar deuda
                                    </div>
                                </button>
                                <button
                                    onClick={() => { setCfMode('nuevo'); setCfCompaneroId(null); setCfCompaneroNombre(''); }}
                                    className={`p-3 rounded-xl text-xs font-black uppercase tracking-tighter border transition-all flex flex-col items-center gap-1 ${cfMode === 'nuevo'
                                        ? 'bg-cyan-500/20 border-cyan-500/60 text-cyan-800 dark:text-cyan-400 shadow-md shadow-cyan-500/20'
                                        : 'border-transparent text-cyan-700 dark:text-cyan-500'}`}
                                    style={{ background: cfMode === 'nuevo' ? 'rgba(6, 182, 212, 0.2)' : 'var(--bg-secondary)' }}
                                >
                                    <div className="flex items-center gap-2">
                                        <UserPlus className="w-3 h-3" /> Otro compañero
                                    </div>
                                </button>
                            </div>

                            {/* Cobrar deuda mode */}
                            {cfMode === 'cobrar' && (
                                <div>
                                    {loadingDeudores ? (
                                        <div className="flex justify-center py-3">
                                            <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
                                        </div>
                                    ) : deudores.length > 0 ? (
                                        <div className="space-y-2">
                                            {deudores.map(d => (
                                                <button
                                                    key={d.companero_id || d.nombre}
                                                    onClick={() => { setCfCompaneroId(d.companero_id); setCfCompaneroNombre(d.nombre); }}
                                                    className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${cfCompaneroId === d.companero_id
                                                        ? 'bg-amber-500/20 border-amber-500/50 text-amber-400'
                                                        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'}`}
                                                >
                                                    <span className="font-bold text-sm">{d.nombre}</span>
                                                    <span className="text-[10px] bg-green-500/20 text-green-400 px-2 py-1 rounded-full font-black">
                                                        Te debe: {d.saldo}
                                                    </span>
                                                </button>
                                            ))}
                                            {cfCompaneroId && (
                                                <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                                                    <Check className="w-3 h-3" /> Se descontará 1 día de la deuda de {cfCompaneroNombre}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="text-center py-4">
                                            <p className="text-xs text-[var(--text-secondary)]">No hay compañeros que te deban días</p>
                                            <button
                                                onClick={() => { setCfMode('nuevo'); setCfCompaneroId(null); }}
                                                className="text-[10px] text-cyan-400 hover:underline mt-2"
                                            >
                                                Usar "Otro compañero" en su lugar
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Nuevo intercambio mode */}
                            {cfMode === 'nuevo' && (
                                <div className="relative">
                                    <div className="flex items-center bg-white/5 border border-white/10 rounded-lg focus-within:border-cyan-500 transition-colors">
                                        <Search className="w-4 h-4 text-[var(--text-muted)] ml-3" />
                                        <input
                                            type="text"
                                            placeholder="Buscar compañero..."
                                            value={ctPeerInput}
                                            onChange={(e) => handleConductorSearch(e.target.value)}
                                            onFocus={() => { if (ctPeerInput.trim().length > 0) setShowCtAutocomplete(true); }}
                                            onBlur={() => setTimeout(() => setShowCtAutocomplete(false), 200)}
                                            className="w-full bg-transparent text-white px-3 py-2 text-sm focus:outline-none"
                                        />
                                    </div>
                                    {renderAutocompleteDropdown()}
                                    {ctPeerId && (
                                        <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                                            <Check className="w-3 h-3" /> Tú le deberás 1 día a {ctPeerInput}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {selectedTurno && (
                        <button
                            onClick={() => setSelectedTurno(null)}
                            className="w-full py-3 rounded-xl text-[10px] font-black text-red-600 dark:text-red-400 uppercase tracking-widest hover:bg-red-500/5 transition-all mt-2 border border-red-500/20"
                        >
                            Restaurar turno original
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
